import { useEffect, useState } from 'react';

const SelectClass = ({
  setDisable,
  classData,
  setClassData,
  courses = [], // ✅ DEFAULT EMPTY ARRAY
  batchStructures = [], // ✅ DEFAULT EMPTY ARRAY
  fetchBatchStructures,
  fetchBatchesByStructure,
  selectedBranch,
  loadingCourses = false,
  clearData,
}) => {
  const [errors, setErrors] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);

  /* ================= COURSE CHANGE ================= */
  const handleCourseChange = async (e) => {
    const courseId = e.target.value;

    setClassData({
      courseId,
      batchStructureId: '',
      batchId: '',
    });

    setBatches([]);
    clearData();

    if (courseId && fetchBatchStructures) {
      await fetchBatchStructures(courseId);
    }
  };

  /* ================= STRUCTURE CHANGE ================= */
  const handleStructureChange = async (e) => {
    const batchStructureId = e.target.value;

    setClassData((prev) => ({
      ...prev,
      batchStructureId,
      batchId: '',
    }));

    setBatches([]);
    clearData();

    if (!batchStructureId) return;

    try {
      setLoadingBatches(true);
      const res = await fetchBatchesByStructure(
        batchStructureId,
        selectedBranch
      );
      setBatches(Array.isArray(res) ? res : []);
    } catch {
      setBatches([]);
    } finally {
      setLoadingBatches(false);
    }
  };

  /* ================= BATCH CHANGE ================= */
  const handleBatchChange = (e) => {
    setClassData((prev) => ({ ...prev, batchId: e.target.value }));
    clearData();
  };

  /* ================= VALIDATION ================= */
  useEffect(() => {
    const invalid =
      !classData.courseId || !classData.batchStructureId || !classData.batchId;

    setErrors(invalid ? ['Please fill in all fields.'] : []);
    setDisable(invalid);
  }, [classData, setDisable]);

  return (
    <div className="w-full rounded-lg px-3 pt-7 md:px-8">
      <h4 className="text-center text-lg font-bold">
        Select Class Information
      </h4>

      <div className="mt-6 flex flex-col md:flex-row md:gap-6">
        {/* ================= COURSE ================= */}
        <div className="w-full md:w-1/3">
          <label className="text-sm font-bold">Select Course</label>
          <select
            value={classData.courseId}
            onChange={handleCourseChange}
            className="mt-2 w-full rounded-xl border p-3"
          >
            <option value="">Select Course</option>

            {loadingCourses ? (
              <option>Loading...</option>
            ) : Array.isArray(courses) && courses.length > 0 ? (
              courses.map((course) => (
                <option key={course._id} value={course._id}>
                  {course.courseName}
                </option>
              ))
            ) : (
              <option disabled>No courses found</option>
            )}
          </select>
        </div>

        {/* ================= STRUCTURE ================= */}
        <div className="w-full md:w-1/3">
          <label className="text-sm font-bold">Batch Structure</label>
          <select
            value={classData.batchStructureId}
            onChange={handleStructureChange}
            disabled={!classData.courseId}
            className="mt-2 w-full rounded-xl border p-3"
          >
            <option value="">Select Structure</option>

            {Array.isArray(batchStructures) && batchStructures.length > 0 ? (
              batchStructures.map((bs) => (
                <option key={bs._id} value={bs._id}>
                  {bs.name}
                </option>
              ))
            ) : (
              <option disabled>No structures found</option>
            )}
          </select>
        </div>

        {/* ================= BATCH ================= */}
        <div className="w-full md:w-1/3">
          <label className="text-sm font-bold">Batch</label>
          <select
            onChange={handleBatchChange}
            value={classData.batchId}
            disabled={!classData.batchStructureId}
            className="mt-2 w-full md:min-w-[400px] rounded-xl border px-3 py-3 text-sm outline-none disabled:opacity-60 dark:bg-navy-800 dark:text-white"
          >
            <option value="">
              {loadingBatches
                ? 'Loading batches...'
                : 'Click here to Select Batch'}
            </option>

            {!loadingBatches && batches.length === 0 && (
              <option disabled>No batches available</option>
            )}

            {!loadingBatches &&
              Array.isArray(batches) &&
              batches.map((b) => {
                const batchDisplay = `B${b.batchNumber}`;
                
                const date = new Date(b.startDate);
                const day = date.getDate().toString().padStart(2, '0');
                const month = date.toLocaleString('default', { month: 'short' });
                const year = date.getFullYear().toString().slice(-2);
                const dateDisplay = `${day}${month}${year}`;

                const dayMap = {
                  Monday: 'M',
                  Tuesday: 'T',
                  Wednesday: 'W',
                  Thursday: 'Th',
                  Friday: 'F',
                  Saturday: 'S',
                  Sunday: 'Su',
                };

                const scheduleDisplay = b.daySchedules
                  ?.map((schedule) => {
                    const dayShort = dayMap[schedule.day] || schedule.day;
                    const time = schedule.startTime.replace(':00', '').replace(' ', '');
                    return `${dayShort}-${time}`;
                  })
                  .join(' | ');

                const branchInitials = b.branches
                  ?.map((br) => br.name.split(',').pop().trim()[0])
                  .join('');

                const schedulePart = scheduleDisplay ? `(${scheduleDisplay} )` : '';
                const fullDisplay = `${batchDisplay} | ${dateDisplay} ${schedulePart} ${branchInitials || ''}`.trim();

                return (
                  <option key={b._id} value={b._id}>
                    {fullDisplay}
                  </option>
                );
              })}
          </select>
        </div>
      </div>

      {errors.length > 0 && <p className="mt-4 text-red-500">{errors[0]}</p>}
    </div>
  );
};

export default SelectClass;
